import { produce } from 'immer';

import { Choice, Experiment, RegionalExperimentConfigValues } from '.';
import {
  CreateExperimentDto,
  ExperimentDto,
  ReviewExperimentDto,
  UpdateExperimentDto,
} from '../../api';
import {
  CustomTimeSliceStrategy,
  ExperimentAssignmentStrategy,
  getAssignmentStrategy,
  getDomain,
  getExperimentEndTimeConstraint,
  getExperimentStartTimeConstraint,
  isBalancedShuffleTimeSliceStrategy,
  isTimeSliceStrategy,
  RestrictedBy,
} from '../../constants';
import { FACETS } from '@/constants/facets';
import { ExperimentAnalysis } from '@/lib/PlannerDTOs';

export function toReviewExperimentDto(
  experiment: Experiment,
  analysis: ExperimentAnalysis,
  configurations: RegionalExperimentConfigValues[],
): ReviewExperimentDto {
  const nextExperimentDto = toExperimentDto(experiment, configurations);
  const nextConfigurations = toRegionalExperimentConfigValuesDto(
    experiment,
    configurations,
  );

  if (nextConfigurations?.length <= 1)
    return {
      experiment: nextExperimentDto,
      analysis_v2: toAnalysisDto(analysis, nextExperimentDto, configurations),
    };

  return {
    experiment: nextExperimentDto,
    analysis_v2: toAnalysisDto(analysis, nextExperimentDto, configurations),
    regional_experiment_config_values: nextConfigurations,
  };
}

export function toCreateExperimentDto(
  experiment: Experiment,
  analysis: ExperimentAnalysis,
  configurations: RegionalExperimentConfigValues[],
): CreateExperimentDto {
  const nextExperimentDto = toExperimentDto(experiment, configurations);
  const nextConfigurations = toRegionalExperimentConfigValuesDto(
    experiment,
    configurations,
  );

  if (configurations?.length <= 1)
    return {
      experiment: nextExperimentDto,
      analysis_v2: toAnalysisDto(analysis, nextExperimentDto, configurations),
    };

  return {
    experiment: nextExperimentDto,
    analysis_v2: toAnalysisDto(analysis, nextExperimentDto, configurations),
    regional_experiment_config_values: nextConfigurations,
  };
}

export function getUpdateExperimentDto(
  experiment: Experiment,
  analysis: ExperimentAnalysis,
  configurations: RegionalExperimentConfigValues[],
): UpdateExperimentDto {
  const nextExperimentDto = toUpdateExperimentDto(experiment, configurations);
  const nextConfigurations = toRegionalExperimentConfigValuesDto(
    experiment,
    configurations,
  );

  if (configurations?.length <= 1)
    return {
      experiment: nextExperimentDto,
      analysis_v2: toAnalysisDto(analysis, nextExperimentDto, configurations),
    };

  return {
    experiment: nextExperimentDto,
    analysis_v2: toAnalysisDto(analysis, nextExperimentDto, configurations),
    regional_experiment_config_values: nextConfigurations,
  };
}

export function toRegionalExperimentConfigValuesDto(
  experiment: Experiment,
  configurations: RegionalExperimentConfigValues[],
): RegionalExperimentConfigValues[] {
  const experimentStrategy = getAssignmentStrategy(configurations);

  const nextConfigurations = produce(configurations, (draft) => {
    let assignByFacets = [getDomain(experiment?.domain)?.targetBy];

    if (
      [
        ExperimentAssignmentStrategy.TIME_SLICE,
        ExperimentAssignmentStrategy.TIME_SLICE_V2,
        ExperimentAssignmentStrategy.TIME_SLICE_SHUFFLE,
      ].includes(experimentStrategy)
    ) {
      assignByFacets = [FACETS.Timestamp];
    } else if (
      experimentStrategy === ExperimentAssignmentStrategy.GEO_TIME_SLICE_SHUFFLE
    ) {
      assignByFacets = [FACETS.Timestamp];
      // if (experimentFormData.strategy.geohash) {
      //   assignByFacets.push(experimentFormData.strategy.geohash);
      // }
    } else if (
      experimentStrategy === ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE
    ) {
      assignByFacets = [FACETS.Timestamp];
    }

    draft.forEach((configuration) => {
      configuration.variables.forEach((variable) => {
        variable.facets = assignByFacets;

        variable?.choices?.forEach((choice: Choice) => {
          switch (experimentStrategy) {
            case ExperimentAssignmentStrategy.UNIFORM_CHOICE:
              delete choice.chance;
              delete choice.span;
              delete choice.spanBuffer;
              delete choice.groupConstraint;
              break;
            case ExperimentAssignmentStrategy.WEIGHTED_CHOICE:
              choice.chance = choice.chance / 100;
              delete choice.span;
              delete choice.groupConstraint;
              delete choice.spanBuffer;
              break;
            case ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE:
              choice.span = experiment?.duration;
              delete choice.chance;
              delete choice.groupConstraint;
              break;
            case ExperimentAssignmentStrategy.EXPLICIT_GROUP:
              choice.group = choice.groupConstraint.value;

              delete choice.chance;
              delete choice.span;
              delete choice.spanBuffer;
              delete choice.groupConstraint;
              break;
          }
        });
      });
    });
  });

  return nextConfigurations;
}

export function toExperimentDto(
  experiment: Experiment,
  configurations: RegionalExperimentConfigValues[],
): ExperimentDto {
  if (!experiment || !configurations) return null;

  const experimentDto: ExperimentDto = {
    name: experiment?.name,
    variables: experiment?.variables,
    constraints: experiment?.constraints,
    state: experiment?.state,
    domain: experiment?.domain,
    slotting: experiment?.slotting,
    metadata: experiment?.metadata,
    createdAt: experiment?.createdAt,
    approvalFlow: experiment?.approvalFlow,
    autoAdaptive: experiment?.autoAdaptive,
    schedulingDefinition: experiment?.schedulingDefinition,
    assignBy: experiment?.assignBy,
    targetBy: experiment?.targetBy,
  };

  const experimentStrategy = getAssignmentStrategy(configurations);

  const nextExperimentDto = produce(experimentDto, (draft) => {
    draft.name = [draft.domain, draft.name].join('.');

    if (!draft.constraints) draft.constraints = [];

    draft.constraints.forEach((constraint) => {
      if (Array.isArray(constraint.value)) {
        constraint.value = `[${constraint.value.join(',')}]`;
      }

      if (constraint.target === RestrictedBy.REGION) {
        constraint.target = RestrictedBy.COUNTRY;
      }
    });

    if (experiment.startTime) {
      const startTimeConstraint = getExperimentStartTimeConstraint(draft);
      if (!startTimeConstraint) {
        draft.constraints.push({
          target: 'ts',
          op: '>',
          value: experiment.startTime.unix().toString(),
        });
      } else {
        startTimeConstraint.value = experiment.startTime.unix().toString();
      }
    }

    if (experiment.endTime) {
      const endTimeConstraint = getExperimentEndTimeConstraint(draft);
      if (!endTimeConstraint) {
        draft.constraints.push({
          target: 'ts',
          op: '<',
          value: experiment.endTime.unix().toString(),
        });
      } else {
        endTimeConstraint.value = experiment.endTime.unix().toString();
      }
    }

    if (!draft.slotting) {
      draft.slotting = {};
    }
    draft.slotting.byPercentage = (experiment.sampleSize ?? 0) / 100;

    if (experiment?.byGroup?.value) {
      draft.slotting.byGroup = experiment.byGroup.value;
      delete draft.slotting.byPercentage;
    }

    draft.variables = toRegionalExperimentConfigValuesDto(
      experiment,
      configurations,
    )?.[0]?.variables;

    if (isBalancedShuffleTimeSliceStrategy(experimentStrategy)) {
      draft.customTimeSliceStrategy = CustomTimeSliceStrategy.BALANCED_SHUFFLE;
    }

    // these fields should be handled by the backend
    // delete draft.schedulingDefinition;
    delete draft.autoAdaptive;
    delete draft.approvalFlow;
    delete draft.state;
  });

  return nextExperimentDto;
}

export function toUpdateExperimentDto(
  experiment: Experiment,
  configurations: RegionalExperimentConfigValues[],
): ExperimentDto {
  if (!experiment || !configurations) return null;

  const experimentDto: ExperimentDto = {
    name: experiment?.name,
    variables: experiment?.variables,
    constraints: experiment?.constraints,
    state: experiment?.state,
    domain: experiment?.domain,
    slotting: experiment?.slotting,
    metadata: experiment?.metadata,
    createdAt: experiment?.createdAt,
    approvalFlow: experiment?.approvalFlow,
    autoAdaptive: experiment?.autoAdaptive,
    schedulingDefinition: experiment?.schedulingDefinition,
    assignBy: experiment?.assignBy,
    targetBy: experiment?.targetBy,
  };

  const experimentStrategy = getAssignmentStrategy(configurations);

  const nextExperimentDto = produce(experimentDto, (draft) => {
    draft.name = [draft.domain, draft.name].join('.');

    if (!draft.constraints) draft.constraints = [];

    draft.constraints.forEach((constraint) => {
      if (Array.isArray(constraint.value)) {
        constraint.value = `[${constraint.value.join(',')}]`;
      }

      if (constraint.target === RestrictedBy.REGION) {
        constraint.target = RestrictedBy.COUNTRY;
      }
    });

    if (experiment.startTime) {
      const startTimeConstraint = getExperimentStartTimeConstraint(draft);
      if (!startTimeConstraint) {
        draft.constraints.push({
          target: 'ts',
          op: '>',
          value: experiment.startTime.unix().toString(),
        });
      } else {
        startTimeConstraint.value = experiment.startTime.unix().toString();
      }
    }

    if (experiment.endTime) {
      const endTimeConstraint = getExperimentEndTimeConstraint(draft);
      if (!endTimeConstraint) {
        draft.constraints.push({
          target: 'ts',
          op: '<',
          value: experiment.endTime.unix().toString(),
        });
      } else {
        endTimeConstraint.value = experiment.endTime.unix().toString();
      }
    }

    if (!draft.slotting) {
      draft.slotting = {};
    }
    draft.slotting.byPercentage = (experiment.sampleSize ?? 0) / 100;

    if (experiment?.byGroup?.value) {
      draft.slotting.byGroup = experiment.byGroup.value;
      delete draft.slotting.byPercentage;
    }

    draft.variables = toRegionalExperimentConfigValuesDto(
      experiment,
      configurations,
    )?.[0]?.variables;

    if (isBalancedShuffleTimeSliceStrategy(experimentStrategy)) {
      draft.customTimeSliceStrategy = CustomTimeSliceStrategy.BALANCED_SHUFFLE;
    }

    // these fields should be handled by the backend
    delete draft.autoAdaptive;
    delete draft.approvalFlow;
    delete draft.state;
  });

  return nextExperimentDto;
}

export function toAnalysisDto(
  analysis: ExperimentAnalysis,
  experiment: Pick<Experiment, 'name' | 'metadata'>,
  configurations?: RegionalExperimentConfigValues[],
): ExperimentAnalysis {
  return produce(analysis, (draft) => {
    const metricTypes = [
      'primary_metrics',
      'secondary_metrics',
      'guardrail_metrics',
      'control_metrics',
    ] as const;

    // Clear control_metrics for non-timeslice strategies
    // Control metrics are only applicable for timeslice experiments
    const strategy = getAssignmentStrategy(configurations || []);
    if (
      !isTimeSliceStrategy(strategy) &&
      draft.analysisConfig?.control_metrics
    ) {
      draft.analysisConfig.control_metrics = [];
    }
    const stripFrontendDimensionFilterFields = (constraint) => {
      const {
        value_input_mode: _valueInputMode,
        validation_status: _validationStatus,
        ...rest
      } = constraint || {};
      return rest;
    };

    // Strip population filter frontend-only fields from all metrics
    const stripPopulationFilterFields = (metric) => {
      const {
        populationDerivedFilters: _populationDerivedFilters,
        prepopulationWarnings: _prepopulationWarnings,
        ...rest
      } = metric || {};
      return rest;
    };

    draft['analysisName'] = experiment?.name;
    const shouldExcludePrimaryMetrics =
      !!experiment?.metadata?.primaryMetricOptOutEnabled;

    if (shouldExcludePrimaryMetrics && draft.analysisConfig?.primary_metrics) {
      draft.analysisConfig.primary_metrics = [];
    }

    metricTypes.forEach((metricType) => {
      const metrics = draft.analysisConfig?.[metricType];
      if (!metrics) return;

      draft.analysisConfig[metricType] = metrics.filter((metric) => {
        const metricName = metric?.name;
        if (typeof metricName === 'string') {
          return metricName.trim().length > 0;
        }

        return !!metricName;
      });

      draft.analysisConfig[metricType].forEach((metric, index) => {
        // Strip population filter frontend-only fields from all metrics
        const sanitizedMetric = stripPopulationFilterFields(metric);

        if (sanitizedMetric.dimension_filters) {
          sanitizedMetric.dimension_filters = sanitizedMetric.dimension_filters
            .map((filter) => ({
              ...filter,
              dimension_filter_constraints:
                filter.dimension_filter_constraints
                  ?.filter((constraint) => !!constraint.dimension_name)
                  .map(stripFrontendDimensionFilterFields) || [],
            }))
            .filter((filter) => filter.dimension_filter_constraints.length > 0);
        }

        draft.analysisConfig[metricType][index] = sanitizedMetric;
      });
    });
  });
}
